
import { v4 as uuidv4 } from 'uuid';
import { BannerTemplate, BannerTemplateFormat } from '@/types/bannerTemplate';
import { BannerSize } from '@/types/banner';
import { saveBannerDraft } from '../bannerCore';
import { getTemplate } from './templateQueries';

/**
 * Save the current banner as a draft template
 */
export const saveAsTemplate = (
  name: string, 
  description: string, 
  category: string, 
  tags: string[], 
  imageUrls: Record<BannerSize, string>, 
  isAnimated: boolean
): BannerTemplate => {
  const templateId = `template-${uuidv4().substring(0, 8)}`;
  
  // Create formats for each size that has an image URL
  const formats: BannerTemplateFormat[] = [];
  
  Object.entries(imageUrls).forEach(([size, url]) => {
    if (url) {
      formats.push({
        size: size as BannerSize,
        imageUrl: url,
        isAnimated,
        originalFormat: isAnimated ? 'gif' : 'png'
      });
    }
  });
  
  // Use the first format as thumbnail, or a default if none
  const thumbnailUrl = formats.length > 0 ? formats[0].imageUrl : 'https://picsum.photos/300/250?random=default';
  
  const newTemplate: BannerTemplate = {
    id: templateId,
    name,
    description,
    thumbnailUrl,
    createdAt: new Date(),
    category,
    tags,
    formats
  };
  
  // In a real app, we would save this to a database or API
  // For now, we'll just return it
  return newTemplate;
};

/**
 * Convert a template to a draft banner
 */
export const templateToDraft = (templateId: string, size: BannerSize): void => {
  const template = getTemplate(templateId);
  if (!template) return;
  
  const format = template.formats.find(f => f.size === size) || template.formats[0];
  if (!format) return;
  
  // Create a draft from the template
  const draft = {
    selectedSize: format.size,
    bannerText: '',
    bannerImage: format.imageUrl,
    backgroundColor: '#ffffff',
    isAnimated: format.isAnimated,
    lastUpdated: Date.now(),
    templateId: template.id,
    templateName: template.name
  };
  
  // Save as draft
  saveBannerDraft('ai_banner_draft', draft);
};
